import { useState } from 'react';
import { Editor } from '@tiptap/react';
import { toast } from 'sonner';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import {
  ToolbarButton,
  Separator,
  ToolbarGroup,
} from './toolbar/ToolbarPrimitives.tsx';
import { TextStyleDropdown } from './toolbar/TextStyleDropdown.tsx';
import { ColorPickerPopover } from './toolbar/ColorPickerPopover.tsx';
import { TEXT_COLORS, HIGHLIGHT_COLORS } from './toolbar/colorOptions.ts';
import TemplateImagePicker from './image/TemplateImagePicker.tsx';
import { TemplateImageSupport } from './image/templateImageSupport.ts';
import {
  DEFAULT_IMAGE_WIDTH,
  probeImageWidth,
} from './image/imageSizing.ts';
import { TemplateImageDto } from '@/features/dashboard/templateImages/types/TemplateImageDto.ts';

const RichTextToolbar = ({
  editor,
  imageSupport,
}: {
  editor: Editor;
  imageSupport?: TemplateImageSupport;
}) => {
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  const insertImage = (image: TemplateImageDto) => {
    // Images can't live inside table cells (they only complicate cell layout and
    // the wrap/align positioning), so block insertion when the caret is in one.
    if (editor.isActive('table')) {
      toast.error('Nie można wstawić grafiki w tabeli');
      setIsImagePickerOpen(false);
      return;
    }
    // Probe natural size to cap (never upscale) before inserting; falls back to
    // the default if the URL can't load (e.g. broken local dev URL).
    probeImageWidth(image.downloadUrl, DEFAULT_IMAGE_WIDTH).then((width) => {
      editor
        .chain()
        .focus()
        .setTemplateImage({
          src: image.downloadUrl,
          alt: image.fileName,
          templateImageId: image.id,
          width,
        })
        .run();
      setIsImagePickerOpen(false);
    });
  };

  const textColor = editor.getAttributes('textStyle').color as
    | string
    | undefined;
  const highlightColor = editor.getAttributes('highlight').color as
    | string
    | undefined;

  // Native browser prompt — minimal UX (can be blocked by browser settings,
  // no validation, looks out of place). Acceptable for now because a proper
  // inline link editor would be a non-trivial component. Replace if link
  // editing becomes a frequent user complaint.
  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-y-1 border-b border-neutral-200 px-2 py-1">
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          isActive={false}
          disabled={!editor.can().undo()}
          title="Cofnij (Ctrl+Z)"
        >
          <Undo2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          isActive={false}
          disabled={!editor.can().redo()}
          title="Ponów (Ctrl+Y)"
        >
          <Redo2 size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Pogrubienie"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Kursywa"
        >
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Podkreślenie"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Przekreślenie"
        >
          <Strikethrough size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <TextStyleDropdown editor={editor} />
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <ColorPickerPopover
          title="Kolor tekstu"
          icon={<Baseline size={16} />}
          colors={TEXT_COLORS}
          currentValue={textColor}
          indicatorFallback="#111827"
          onSelect={(color) => {
            const chain = editor.chain().focus();
            if (color) chain.setColor(color).run();
            else chain.unsetColor().run();
          }}
        />
        <ColorPickerPopover
          title="Podświetlenie"
          icon={<Highlighter size={16} />}
          colors={HIGHLIGHT_COLORS}
          currentValue={highlightColor}
          indicatorFallback="transparent"
          onSelect={(color) => {
            const chain = editor.chain().focus();
            if (color) chain.setHighlight({ color }).run();
            else chain.unsetHighlight().run();
          }}
        />
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista punktowana"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista numerowana"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Wyrównaj do lewej"
        >
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Wyśrodkuj"
        >
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Wyrównaj do prawej"
        >
          <AlignRight size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          title="Justuj"
        >
          <AlignJustify size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <Separator />
        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <Link2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          isActive={false}
          title="Linia pozioma"
        >
          <Minus size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          isActive={editor.isActive('table')}
          title="Wstaw tabelę (3×3)"
        >
          <TableIcon size={16} />
        </ToolbarButton>
        {imageSupport && (
          <ToolbarButton
            onClick={() => setIsImagePickerOpen(true)}
            isActive={isImagePickerOpen}
            title="Wstaw grafikę"
          >
            <ImageIcon size={16} />
          </ToolbarButton>
        )}
      </ToolbarGroup>
      {imageSupport && isImagePickerOpen && (
        <TemplateImagePicker
          imageSupport={imageSupport}
          onInsert={insertImage}
          onClose={() => setIsImagePickerOpen(false)}
        />
      )}
    </div>
  );
};

export default RichTextToolbar;
